import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { useApi } from '../../hooks';
import { useTacticalTimer } from '../../hooks/useTacticalTimer';

interface ShiftDefinition {
  hour: number;
  minute: number;
  label: string;
}

interface ShiftsResponse {
  timezone: string;
  is_friday: boolean;
  shifts: {
    morning_in: ShiftDefinition;
    morning_out: ShiftDefinition;
    evening_in: ShiftDefinition;
    evening_out: ShiftDefinition;
  };
  grace_window_minutes: number;
}

interface CheckInResponse {
  employee: string;
  shift_type: string;
  scheduled_time: string;
  actual_time: string;
  minutes_offset: number;
  within_grace: boolean;
  tactical_delay: boolean;
  early_arrival: boolean;
  log_id: string;
  message: string;
}

interface AttendanceTrackingProps {
  user?: { name: string; role: string; phone: string };
}

const AttendanceTracking: React.FC<AttendanceTrackingProps> = ({ user }) => {
  // Identity comes from the authenticated session — never a dropdown
  const employeeName = user?.name || '';
  const [selectedShift, setSelectedShift] = useState<string>('');
  const [checkInResult, setCheckInResult] = useState<CheckInResponse | null>(null);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: shiftsData, loading: shiftsLoading } = useApi<ShiftsResponse>(
    () => api.employee.shifts()
  );

  const tacticalTimer = useTacticalTimer(employeeName, !!employeeName);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleCheckIn = async () => {
    if (!employeeName || !selectedShift) {
      alert('Please select your name and shift type');
      return;
    }

    setIsSubmitting(true);
    setCheckInResult(null);

    try {
      const result = await api.employee.checkIn({
        employee_name: employeeName,
        shift_type: selectedShift,
      });
      setCheckInResult(result);
      tacticalTimer.updateLastActivity();
    } catch (error: any) {
      alert(`Check-in failed: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (hour: number, minute: number) => {
    const h = hour > 12 ? hour - 12 : hour;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${h.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} ${ampm}`;
  };

  const getDubaiTime = () => {
    return currentTime.toLocaleTimeString('en-US', {
      timeZone: 'Asia/Dubai',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const getDubaiDate = () => {
    return currentTime.toLocaleDateString('en-US', {
      timeZone: 'Asia/Dubai',
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const containerStyle: React.CSSProperties = {
    backgroundColor: tacticalTimer.isInactive ? '#ffd6a5' : 'transparent',
    transition: 'background-color 0.5s ease',
  };

  return (
    <div className="attendance-tracking" style={containerStyle}>
      <h1 className="page-title">Attendance Tracking</h1>

      {tacticalTimer.isInactive && (
        <div className="notification notification--warning">
          <strong>⚠️ UNEXCUSED ABSENCE DETECTED</strong>
          <p>Away from station for {tacticalTimer.inactiveMinutes} minutes</p>
        </div>
      )}

      <div className="card">
        <div className="attendance-header">
          <div className="time-display">
            <h2>{getDubaiTime()}</h2>
            <p className="text-muted">{getDubaiDate()}</p>
            {shiftsData?.is_friday && (
              <span className="badge badge--info">🕌 Friday Protocol Active</span>
            )}
          </div>
        </div>
      </div>

      {shiftsLoading ? (
        <div className="card">
          <p className="text-muted">Loading shift schedule...</p>
        </div>
      ) : shiftsData ? (
        <>
          <div className="card">
            <h3>📅 Today's Shift Schedule</h3>
            <div className="shift-schedule">
              <div className="shift-item">
                <span className="shift-label">Morning In</span>
                <span className="shift-time">
                  {formatTime(shiftsData.shifts.morning_in.hour, shiftsData.shifts.morning_in.minute)}
                </span>
                <span className="shift-desc">The "09:00 Mandate" – Tactical start</span>
              </div>
              <div className="shift-item">
                <span className="shift-label">Morning Out</span>
                <span className="shift-time">
                  {formatTime(shiftsData.shifts.morning_out.hour, shiftsData.shifts.morning_out.minute)}
                </span>
                <span className="shift-desc">
                  {shiftsData.is_friday ? 'Friday: Early dismissal' : 'Mid-day break / Siesta'}
                </span>
              </div>
              <div className="shift-item">
                <span className="shift-label">Evening In</span>
                <span className="shift-time">
                  {formatTime(shiftsData.shifts.evening_in.hour, shiftsData.shifts.evening_in.minute)}
                </span>
                <span className="shift-desc">Resume operations for evening peak</span>
              </div>
              <div className="shift-item">
                <span className="shift-label">Evening Out</span>
                <span className="shift-time">
                  {formatTime(shiftsData.shifts.evening_out.hour, shiftsData.shifts.evening_out.minute)}
                </span>
                <span className="shift-desc">EOD Sync & Shutdown</span>
              </div>
            </div>
            <p className="text-muted" style={{ marginTop: '16px' }}>
              Grace Window: {shiftsData.grace_window_minutes} minutes
            </p>
          </div>

          <div className="card">
            <h3>🔐 Check-In Terminal</h3>

            {/* Identity from session — no dropdown */}
            <div className="form-group">
              <label>Signed in as</label>
              <div style={{
                padding: '12px 16px',
                background: 'rgba(184,213,141,0.08)',
                border: '1px solid rgba(184,213,141,0.2)',
                borderRadius: 8,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span style={{ fontSize: 20 }}>
                  {user?.role === 'owner' ? '👑' : user?.role === 'manager' ? '🏛️' : '👤'}
                </span>
                <div>
                  <div style={{ fontWeight: 700, color: '#dae2fd', fontSize: 15, fontFamily: "'Manrope', sans-serif" }}>
                    {employeeName || 'Unknown'}
                  </div>
                  <div style={{ fontSize: 11, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: "'Space Grotesk', sans-serif" }}>
                    {user?.role || 'employee'}
                  </div>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                  <span style={{
                    background: 'rgba(184,213,141,0.15)', color: '#b8d58d',
                    borderRadius: 99, padding: '2px 10px', fontSize: 11, fontWeight: 600,
                    fontFamily: "'Space Grotesk', sans-serif",
                  }}>
                    ✓ Verified
                  </span>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Shift Type</label>
              <div className="shift-buttons">
                <button
                  className={`btn ${selectedShift === 'morning_in' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setSelectedShift('morning_in')}
                >
                  Morning In
                </button>
                <button
                  className={`btn ${selectedShift === 'morning_out' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setSelectedShift('morning_out')}
                >
                  Morning Out
                </button>
                <button
                  className={`btn ${selectedShift === 'evening_in' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setSelectedShift('evening_in')}
                >
                  Evening In
                </button>
                <button
                  className={`btn ${selectedShift === 'evening_out' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setSelectedShift('evening_out')}
                >
                  Evening Out
                </button>
              </div>
            </div>

            <button
              className="btn btn-primary btn-large"
              onClick={handleCheckIn}
              disabled={!employeeName || !selectedShift || isSubmitting}
              title={!employeeName ? 'No authenticated session' : ''}
            >
              {isSubmitting ? 'Checking In...' : '✓ Check In Now'}
            </button>
          </div>

          {checkInResult && (
            <div className={`card notification ${
              checkInResult.tactical_delay 
                ? 'notification--error' 
                : checkInResult.early_arrival 
                ? 'notification--info' 
                : 'notification--success'
            }`}>
              {checkInResult.tactical_delay ? (
                <>
                  <h3>🚨 TACTICAL DELAY</h3>
                  <p><strong>Alert sent to Commander (Masoomi)</strong></p>
                  <p>Late by {checkInResult.minutes_offset} minutes</p>
                  <p className="text-muted">Log ID: {checkInResult.log_id}</p>
                </>
              ) : checkInResult.early_arrival ? (
                <>
                  <h3>✅ Early Arrival</h3>
                  <p>{checkInResult.message}</p>
                  <p>Arrived {Math.abs(checkInResult.minutes_offset)} minutes early</p>
                  <p className="text-muted">Log ID: {checkInResult.log_id}</p>
                </>
              ) : (
                <>
                  <h3>✅ On Time</h3>
                  <p>{checkInResult.message}</p>
                  <p className="text-muted">Log ID: {checkInResult.log_id}</p>
                </>
              )}
            </div>
          )}

          <div className="card">
            <h3>📡 Tactical Timer Status</h3>
            <div className="status-grid">
              <div className="status-item">
                <span className="status-label">Connection</span>
                <span className={`status-badge status-badge--${tacticalTimer.connectionStatus}`}>
                  {tacticalTimer.connectionStatus}
                </span>
              </div>
              <div className="status-item">
                <span className="status-label">Last Ping</span>
                <span className="status-value">
                  {tacticalTimer.lastPingTime 
                    ? tacticalTimer.lastPingTime.toLocaleTimeString() 
                    : 'Not yet pinged'}
                </span>
              </div>
              <div className="status-item">
                <span className="status-label">Inactivity Status</span>
                <span className={`status-badge ${tacticalTimer.isInactive ? 'status-badge--warning' : 'status-badge--success'}`}>
                  {tacticalTimer.isInactive ? `Inactive (${tacticalTimer.inactiveMinutes}m)` : 'Active'}
                </span>
              </div>
            </div>
            <p className="text-muted" style={{ marginTop: '16px' }}>
              System pings every 10 minutes. Inactivity alert triggers after 15 minutes.
            </p>
          </div>
        </>
      ) : (
        <div className="card">
          <p className="text-muted">Failed to load shift schedule</p>
        </div>
      )}
    </div>
  );
};

export default AttendanceTracking;
